/**
 * ClientInsightsPanel - Displays key client-level insights
 * TASK-206: Client Level View - Multi-Portfolio Comparison
 *
 * Shows concentration risks, cash position, and diversification gaps
 * across all client portfolios
 */

import React from 'react';
import { AlertTriangle, DollarSign, Target, TrendingDown, MapPin, Activity, Globe, Droplets } from 'lucide-react';
import { ClientInsights } from '../../hooks/useClientLevelData';
import Badge from './atoms/Badge';

export interface ClientInsightsPanelProps {
  insights: ClientInsights;
  className?: string;
}

const ClientInsightsPanel: React.FC<ClientInsightsPanelProps> = ({
  insights,
  className = ''
}) => {
  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `CHF ${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `CHF ${(value / 1000).toFixed(0)}K`;
    }
    return `CHF ${value.toLocaleString('de-CH')}`;
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity.toUpperCase()) {
      case 'HIGH':
        return <Badge label="HIGH" variant="error" size="sm" />;
      case 'MEDIUM':
        return <Badge label="MEDIUM" variant="warning" size="sm" />;
      case 'LOW':
        return <Badge label="LOW" variant="success" size="sm" />;
      default:
        return <Badge label={severity} variant="default" size="sm" />;
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Row 1: Main Insights (3 columns) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Concentration Risks Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Concentration Risks</h3>
            <p className="text-xs text-gray-500">High-impact positions</p>
          </div>
        </div>

        {insights.concentrationRisks && insights.concentrationRisks.length > 0 ? (
          <div className="space-y-3">
            {insights.concentrationRisks.slice(0, 3).map((risk, idx) => (
              <div key={idx} className="border-l-4 border-red-500 pl-3 py-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {risk.title}
                  </span>
                  {getSeverityBadge(risk.severity)}
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  {risk.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-red-600">
                    {formatCurrency(risk.value)}
                  </span>
                  <span className="text-xs font-bold text-red-700">
                    {risk.percentage.toFixed(1)}%
                  </span>
                </div>
                {risk.recommendation && (
                  <p className="text-xs text-gray-500 mt-2 italic">
                    → {risk.recommendation}
                  </p>
                )}
              </div>
            ))}
            {insights.concentrationRisks.length > 3 && (
              <p className="text-xs text-gray-500 text-center pt-2">
                +{insights.concentrationRisks.length - 3} more risk{insights.concentrationRisks.length - 3 !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-md p-3 text-center">
            <p className="text-sm text-green-700">No significant concentration risks</p>
          </div>
        )}
      </div>

      {/* Cash Position Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Cash Position</h3>
            <p className="text-xs text-gray-500">Liquidity & opportunity cost</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Total Cash */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-3xl font-bold text-blue-600">
                {formatCurrency(insights.cashPosition.total)}
              </span>
              <span className="text-sm font-semibold text-gray-600">
                {insights.cashPosition.percentage.toFixed(1)}%
              </span>
            </div>
            <p className="text-xs text-gray-500">Total cash across portfolios</p>
          </div>

          {/* Breakdown by Portfolio */}
          {insights.cashPosition.breakdown && insights.cashPosition.breakdown.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-700">By Portfolio:</p>
              {insights.cashPosition.breakdown.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">{item.portfolio}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {formatCurrency(item.amount)}
                    </span>
                    <span className="text-gray-500">({item.percentage.toFixed(1)}%)</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Opportunity Cost */}
          {(insights.cashPosition.opportunityCost ?? 0) > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-4 h-4 text-yellow-600" />
                <span className="text-xs font-semibold text-yellow-800">Opportunity Cost</span>
              </div>
              <p className="text-xs text-yellow-700">
                ~{formatCurrency(insights.cashPosition.opportunityCost ?? 0)}/year
              </p>
            </div>
          )}

          {/* Recommendation */}
          {insights.cashPosition.recommendation && (
            <p className="text-xs text-gray-600 italic pt-2 border-t border-gray-100">
              → {insights.cashPosition.recommendation}
            </p>
          )}
        </div>
      </div>

      {/* Diversification Gaps Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
            <Target className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Diversification Gaps</h3>
            <p className="text-xs text-gray-500">Missing exposures</p>
          </div>
        </div>

        {insights.diversificationGaps && insights.diversificationGaps.length > 0 ? (
          <div className="space-y-3">
            {insights.diversificationGaps.map((gap, idx) => (
              <div key={idx} className="border-l-4 border-purple-500 pl-3 py-2">
                <div className="flex items-center gap-2 mb-1">
                  {gap.type === 'GEOGRAPHIC' && <MapPin className="w-3.5 h-3.5 text-purple-600" />}
                  {gap.type === 'SECTOR' && <Activity className="w-3.5 h-3.5 text-purple-600" />}
                  <span className="text-xs font-semibold text-purple-700">
                    {gap.type === 'GEOGRAPHIC' ? gap.region : gap.sector}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  {gap.description}
                </p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Current:</span>
                  <span className="font-semibold text-gray-900">
                    {gap.currentExposure.toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Recommended:</span>
                  <span className="font-semibold text-purple-600">
                    {gap.recommendedExposure.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
            {insights.diversificationGaps.length > 3 && (
              <p className="text-xs text-gray-500 text-center pt-2">
                +{insights.diversificationGaps.length - 3} more gap{insights.diversificationGaps.length - 3 !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-md p-3 text-center">
            <p className="text-sm text-green-700">Well diversified portfolio</p>
          </div>
        )}
      </div>
      </div>

      {/* Row 2: Currency Risk & Liquidity (2 columns) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Currency Risk Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Currency Risk</h3>
              <p className="text-xs text-gray-500">FX exposure analysis</p>
            </div>
          </div>

          {insights.currencyRisk && (
            <div className="space-y-4">
              {/* USD Exposure */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">USD Exposure</span>
                  <span className="text-2xl font-bold text-indigo-600">
                    {insights.currencyRisk.usdExposure.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full"
                    style={{ width: `${insights.currencyRisk.usdExposure}%` }}
                  />
                </div>
              </div>

              {/* FX Impact Scenario */}
              {insights.currencyRisk.fxImpactScenario && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="flex items-start gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-yellow-800">FX Risk Scenario</p>
                      <p className="text-xs text-yellow-700 mt-1">
                        If USD/CHF drops 10%: <strong>{insights.currencyRisk.fxImpactScenario.usdChfDrop10Pct.toFixed(1)}%</strong> portfolio impact
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs mt-2">
                    <span className="text-yellow-700">Current Hedge:</span>
                    <span className="font-semibold text-yellow-800">
                      {insights.currencyRisk.fxImpactScenario.currentHedge.toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}

              {/* Recommendation */}
              {insights.currencyRisk.fxImpactScenario?.recommendation && (
                <p className="text-xs text-gray-600 italic pt-2 border-t border-gray-100">
                  → {insights.currencyRisk.fxImpactScenario.recommendation}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Liquidity Position Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-cyan-50 rounded-lg flex items-center justify-center">
              <Droplets className="w-5 h-5 text-cyan-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Liquidity Position</h3>
              <p className="text-xs text-gray-500">Asset liquidity breakdown</p>
            </div>
          </div>

          {insights.liquidityPosition && (
            <div className="space-y-4">
              {/* Immediate Liquidity */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full" />
                    <span className="text-sm text-gray-700">Immediate (Cash)</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">
                    {insights.liquidityPosition.immediate.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: `${insights.liquidityPosition.immediate}%` }}
                  />
                </div>
              </div>

              {/* T+2 Liquidity */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full" />
                    <span className="text-sm text-gray-700">T+2 (Stocks/ETFs)</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">
                    {insights.liquidityPosition.tPlus2.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${insights.liquidityPosition.tPlus2}%` }}
                  />
                </div>
              </div>

              {/* Low Liquidity */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full" />
                    <span className="text-sm text-gray-700">Low (Funds/Illiquid)</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">
                    {insights.liquidityPosition.lowLiquidity.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full"
                    style={{ width: `${insights.liquidityPosition.lowLiquidity}%` }}
                  />
                </div>
              </div>

              {/* Liquidity Warning */}
              {insights.liquidityPosition.lowLiquidity > 40 && (
                <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                  <p className="text-xs text-orange-800">
                    <strong>Note:</strong> {insights.liquidityPosition.lowLiquidity.toFixed(0)}% of assets have
                    limited liquidity. Consider maintaining adequate liquid reserves.
                  </p>
                </div>
              )}

              {/* Total Liquidity Summary */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Total Liquid (Immediate + T+2):</span>
                  <span className="font-bold text-cyan-700">
                    {(insights.liquidityPosition.immediate + insights.liquidityPosition.tPlus2).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientInsightsPanel;
